import React, { useRef, useState, useEffect } from "react";
import {
  View,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Pressable,
} from "react-native";
import variables from "../../styles/variables";
import Icon from "../Icon";
import RText from "../RText";
import { formatRaceTime } from "../../utils/TimeCalculator";
import { getUpNext } from "../../client/watch/live";
import { ScheduleSlotItem } from "../../types/tvSchedule";
import { Status } from "../../types/status";
import usePressableProps from "../../hooks/usePressableProps";

const { spacing, colors } = variables;

export type RaceState = Status;

export type UpcomingRace = {
  country: string;
  distance_formatted: string;
  eligibility_formatted: string;
  icon: string;
  id: number;
  meeting_date: string;
  prize_text: string;
  race_class: null;
  race_type: string;
  rating_limit_range: null;
  runner_count_text: string;
  slug: string;
  start_time_scheduled: string;
  status: { state: RaceState };
  title: string;
  track_condition: string;
  track_name: string;
  track_slug: string;
  track_type: string;
  tv_channel: string;
};

const ComingupBar = (style: any) => {
  const [currentScrollPosX, setCurrentScrollPosX] = useState(0);
  const [showRightScrollButton, setShowRightScrollButton] =
    useState<boolean>(true);

  const [upNextItems, setUpNextItems] = useState<ScheduleSlotItem[]>([]);
  const [error, setError] = useState(null);

  const pressableProps = usePressableProps({
    screen: "Watch",
  });

  useEffect(() => {
    getUpNext(false)
      .then((res) => setUpNextItems(res))
      .catch((err: Error) => setError(err.message));
  }, []);

  // TODO - we should separate this frontend logic from the business logic in here
  const ref = useRef<ScrollView>(null);
  const scrollHorizontalLeft = (scrollOffset) => {
    const newScrollPosX = currentScrollPosX - scrollOffset;
    ref.current.scrollTo({ x: newScrollPosX, animated: true });
    setCurrentScrollPosX(newScrollPosX);
  };
  const scrollHorizontalRight = (scrollOffset) => {
    const newScrollPosX = scrollOffset + currentScrollPosX;
    ref.current.scrollTo({ x: newScrollPosX, animated: true });
    setCurrentScrollPosX(newScrollPosX);
  };
  const isScrollEnd = ({ layoutMeasurement, contentOffset, contentSize }) => {
    const paddingToRight = 10;
    return (
      layoutMeasurement.width + contentOffset.x >=
      contentSize.width - paddingToRight
    );
  };

  const renderUpcomingSchedule = (items: ScheduleSlotItem[]) => {
    if (!items) return;

    return items.map((item, i) => {
      return (
        <TouchableOpacity
          style={styles.scheduleContainer}
          key={i}
          {...pressableProps}
        >
          <RText
            family="primary"
            color="white"
            size="md"
            weight="semiBold"
            capitalize
          >
            {item === items[0]
              ? "Live Now"
              : formatRaceTime(item.startAt.toString())}
          </RText>
          <RText
            family="primary"
            color="tertiary"
            size="md"
            weight="semiBold"
            capitalize
            style={text.item}
          >
            {item.title}
          </RText>
        </TouchableOpacity>
      );
    });
  };

  const renderButton = (location: string) => {
    if (location === "left" && currentScrollPosX > 0) {
      return (
        <Pressable
          style={[styles.button]}
          onPress={() => scrollHorizontalLeft(200)}
        >
          <Icon
            name="chevronLeft"
            size={"small"}
            fill={"palette.rtv.white"} // palette.buttons.default.color"}
          />
        </Pressable>
      );
    }
    if (location === "right") {
      return (
        <View style={{ width: 60, height: 60 }}>
          {showRightScrollButton && (
            <TouchableOpacity
              style={styles.button}
              onPress={() => scrollHorizontalRight(200)}
            >
              <Icon
                name="chevronRight"
                size={"small"}
                fill={"palette.buttons.default.color"}
              />
            </TouchableOpacity>
          )}
        </View>
      );
    }
  };

  if (!upNextItems) return;

  return (
    <View style={[styles.comingupBarContainer, style]}>
      <View style={styles.comingupBarLeftContainer}>
        <View style={{ width: "80%", alignItems: "center" }}>
          <RText
            family="secondary"
            color="white"
            size="xxl"
            weight="bold"
            uppercase
          >
            COMING UP
          </RText>
        </View>
        <View
          style={{
            width: "20%",
            alignItems: "center",
            marginRight: spacing.small,
          }}
        >
          {renderButton("left")}
        </View>
      </View>

      <View style={styles.comingupBarRightContainer}>
        <ScrollView
          horizontal
          contentContainerStyle={styles.comingupBarRaces}
          showsHorizontalScrollIndicator={false}
          scrollEventThrottle={200}
          decelerationRate="fast"
          pagingEnabled
          ref={ref}
          onScroll={({ nativeEvent }) => {
            isScrollEnd(nativeEvent)
              ? setShowRightScrollButton(false)
              : setShowRightScrollButton(true);
          }}
        >
          {renderUpcomingSchedule(upNextItems)}
        </ScrollView>
        <View>
          {renderButton("right") || <View style={{ width: 60, height: 60 }} />}
        </View>
      </View>
    </View>
  );
};

const text = StyleSheet.create({
  item: {
    marginLeft: 5,
  },
});

const styles = StyleSheet.create({
  comingupBarContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
    height: 60,
    backgroundColor: "rgba(29, 28, 57, 0.5)",
  },
  comingupBarLeftContainer: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    width: "25%",
    paddingLeft: spacing.xsmall,
  },
  comingupBarRightContainer: {
    flexDirection: "row",
    width: "75%",
  },
  comingupBarRaces: {
    flexDirection: "row",
    justifyContent: "space-around",
    alignItems: "center",
  },
  scheduleContainer: {
    flexDirection: "row",
    marginRight: 45,
  },
  button: {
    justifyContent: "center",
    alignItems: "center",
    height: 60,
    width: 60,
    backgroundColor: colors.palette.rtv.primary,
    opacity: 0.3,
  },
});

export default ComingupBar;
